import { useCallback, useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useBooleanState } from '@hqo/react-components-library';
import { useQueryClient } from '@tanstack/react-query';
import { IN_APP_CONTENT, UTILITY_NAMES, WEB_APP_NATIVE_PAGES } from 'app/components/attachable/types';
import { CtaTypeEnum, TenantContentData } from 'app/data/types/content';
import { useBuilding } from 'app/shared/contexts/building';

import { useUpdateEventPostMutation, UPDATE_EVENT_POST_TYPE } from 'app/data/mutations/use-update-event-post-mutation';
import { useUpdateOfferMutation, UPDATE_OFFER_TYPE } from 'app/data/mutations/use-update-offer-mutation';
import { useSpotlightCategoriesQuery } from 'app/data/queries/use-spotlight-categories-query';

import { useMicroFrontendParams } from 'app/shared/hooks/use-micro-frontend-params';
import { HEADER_BUTTON_MODE, LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useContent } from 'app/shared/contexts/content';
import { useIntl } from 'react-intl';
import { useTrackWebClick } from 'app/shared/hooks/use-track-web-click';
import { WebClickClickTypeUnion } from 'app/data/types/tracking/web-click';
import { useSdkHostInfo } from 'app/shared/hooks/use-sdk-host-info';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';
import { HOST_PLATFORMS } from 'app/shared/consts';

const ATTACHABLE_ELEMENT_NAME = 'ATTACHABLE_CTA';

interface Props {
  attachable: TenantContentData;
  isMobile: boolean | undefined;
}

interface Response {
  isVisible: boolean;
  isConfirmedModalVisible: boolean;
  showCancelClaimButton: boolean;
  onCancelClaimClick: VoidFunction;
  showCancelTextOverride: boolean;
  onCancelClick: VoidFunction;
  buttonIsLoading: boolean;
  onClickButton: VoidFunction;
  onCtaClick: VoidFunction;
  isClaimable: boolean;
  isExternalUrl: boolean;
  hasOpenExternalLink: boolean;
  updateEventPostStatus: string;
  updateOfferStatus: string;
  errorMessage: string | undefined;
  ctaUrl: string;
}

export const useAttachable = ({ attachable, isMobile }: Props): Response => {
  const intl = useIntl();
  const location = useLocation();
  const { building } = useBuilding();
  const { content } = useContent();
  const contentUuid = content?.uuid;
  const { hostPlatform } = useSdkHostInfo();
  const { availableCmds } = useSdkData();
  const hasOpenExternalLink = useMemo(() => availableCmds.includes('openExternalLink'), [availableCmds]);
  const trackWebClick = useTrackWebClick();
  const baseTrackingProperties = useMemo(() => ({
    route: location.pathname,
    element_name: ATTACHABLE_ELEMENT_NAME,
    click_type: 'action' as WebClickClickTypeUnion,
    destination_route: null,
    additional_properties: {
      content_uuid: contentUuid ?? '',
      cta_button_label: attachable.cta_label ?? '',
      cta_type: attachable.cta_type ?? '',
      building_uuid: building?.uuid ?? '',
      host_platform: hostPlatform ?? HOST_PLATFORMS.MOBILE,
    },
  }), [location.pathname, contentUuid, attachable.cta_type, attachable.cta_label, building, hostPlatform]);
  const queryClient = useQueryClient();
  const { value: isVisible, toggle } = useBooleanState();
  const { value: showCancelTextOverride, setTrue: enableCancelTextOverride, setFalse: disableCancelTextOverride } = useBooleanState(false);
  const { value: isConfirmedModalVisible, setTrue: setConfirmedModalTrue, setFalse: setConfirmedModalFalse } = useBooleanState();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [updateEventPostType, setUpdateEventPostType] = useState<UPDATE_EVENT_POST_TYPE | undefined>(undefined);
  const [updateOfferType, setUpdateOfferType] = useState<UPDATE_OFFER_TYPE | undefined>(undefined);

  const { mutate: updateEventPost, status: updateEventPostStatus, reset: resetUpdateEventPost } = useUpdateEventPostMutation();
  const { mutate: updateOffer, status: updateOfferStatus, reset: resetUpdateOffer } = useUpdateOfferMutation();
  const { data: spotlightCategories } = useSpotlightCategoriesQuery();
  const navigate = useNavigate();

  const sdkClient = useMiniappSdk();

  const getInternalLink: () => string = useCallback((): string => {
    if (attachable.attachable_amenity_uuid) {
      // only works in mobile as web app doesn't have amenities
      const navigation = {
        value: IN_APP_CONTENT.AMENITIES,
        type: LinkTypeEnum.NATIVE_SCREEN,
        options: {
          url: attachable.attachable_amenity_uuid,
          building_uuid: building?.uuid,
          uuid: attachable.attachable_amenity_uuid,
        },
      };
      sdkClient?.redirectToContent(navigation);

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: `/amenities/${attachable.attachable_amenity_uuid}`,
      });

      return UTILITY_NAMES.AMENITIES;
    }
    if (attachable.attachable_company_uuid) {
      // only works in mobile as web app doesn't have companies
      const navigation = {
        value: IN_APP_CONTENT.COMPANIES,
        type: LinkTypeEnum.NATIVE_SCREEN,
        options: {
          url: attachable.attachable_company_uuid,
          building_uuid: building?.uuid,
          uuid: attachable.attachable_company_uuid,
        },
      };

      sdkClient?.redirectToContent(navigation);

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: `/companies/${attachable.attachable_company_uuid}`,
      });

      return UTILITY_NAMES.COMPANIES;
    }
    if (attachable.attachable_content_uuid) {
      sdkClient?.header?.setHeader('', HEADER_BUTTON_MODE.HISTORY);
      const searchParams = window.location.search;
      const nextRoute = `/content/${attachable.attachable_content_uuid}${searchParams || ''}`;

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: nextRoute,
      });
      if (hostPlatform === HOST_PLATFORMS.TENANT_PORTAL) {
        sdkClient?.redirectToContent({
          value: WEB_APP_NATIVE_PAGES.FEED,
          type: LinkTypeEnum.NATIVE_SCREEN,
          options: {
            uuid: attachable.attachable_content_uuid,
            building_uuid: building?.uuid,
          },
        });
      } else {
        navigate(nextRoute);
      }
    }
    if (attachable.attach_secondary_content) {
      const spotlightCategoryUuid = spotlightCategories?.[0]?.uuid;
      // Web + tenant-portal hand off to the host via the SDK, which routes to its secondary-content
      // section. Native (iOS/Android) route this command to an error screen, so the CTA is a no-op
      // there. tenant-web has no redirectToContent handler yet (follow-up) — until then it safely
      // no-ops the command (its SDK event enum excludes redirectToContent, so it's filtered).
      const isSecondaryContentHost = hostPlatform === HOST_PLATFORMS.WEB || hostPlatform === HOST_PLATFORMS.TENANT_PORTAL;
      if (spotlightCategoryUuid && isSecondaryContentHost) {
        sdkClient?.redirectToContent({
          value: WEB_APP_NATIVE_PAGES.SECONDARY_CONTENT,
          type: LinkTypeEnum.NATIVE_SCREEN,
          options: {
            uuid: spotlightCategoryUuid,
            building_uuid: building?.uuid,
          },
        });
        // Each host maps the SECONDARY_CONTENT command to its own route: tenant-portal to the
        // secondary-feed page filtered by category, web to the building page's category anchor.
        const secondaryContentRoute = hostPlatform === HOST_PLATFORMS.TENANT_PORTAL
          ? `/building/${building?.uuid}/secondary-feed?secondary-content-category=${spotlightCategoryUuid}`
          : `/building/${building?.uuid}#${spotlightCategoryUuid}`;
        trackWebClick({
          ...baseTrackingProperties,
          click_type: 'navigation',
          destination_route: secondaryContentRoute,
        });
      }
    }
    return '';
  }, [attachable, building?.uuid, sdkClient, trackWebClick, baseTrackingProperties, hostPlatform, navigate, spotlightCategories]);

  const getParams = useMicroFrontendParams();

  const ctaUrl = useMemo(() => (
    attachable.cta_url?.includes('https')
    || attachable.cta_url?.includes('mailto:')
    || attachable.cta_url?.includes('tel:')
  ? attachable.cta_url : `https://${attachable.cta_url}`), [attachable.cta_url]);

  const getMicroFrontend: () => string = useCallback((): string => {
    const nextRoute = `${ctaUrl}?${getParams}`;
    trackWebClick({
      ...baseTrackingProperties,
      click_type: 'navigation',
      destination_route: nextRoute,
      additional_properties: {
        ...baseTrackingProperties.additional_properties,
        cta_url: ctaUrl,
      },
    });

    if (hostPlatform === HOST_PLATFORMS.TENANT_PORTAL) {
      sdkClient?.redirectToContent({
        value: '',
        type: LinkTypeEnum.WEBVIEW,
        options: {
          url: ctaUrl,
        },
      });
    } else {
      window.open(nextRoute, '_self');
    }

    sdkClient?.header?.showHeader();
    sdkClient?.header?.setHeader('', HEADER_BUTTON_MODE.HISTORY);

    return UTILITY_NAMES.TENANT_SATISFACTION;
  }, [baseTrackingProperties, ctaUrl, getParams, hostPlatform, sdkClient, trackWebClick]);

  const isExternalUrl = useMemo(() => Boolean(
    (attachable.cta_type === CtaTypeEnum.EXTERNAL_URL || attachable.cta_type === CtaTypeEnum.DEEP_LINK)
    && !ctaUrl?.includes('mfe-survey')),
    [attachable.cta_type, ctaUrl]);

  const getExternalUrlOrDeepLink: () => string = useCallback((): string => {
    if (ctaUrl?.includes('mfe-survey')) {
      const nextRoute = `${ctaUrl}?${getParams}`;
      window.open(nextRoute, '_self');

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: nextRoute,
        additional_properties: {
          ...baseTrackingProperties.additional_properties,
          cta_url: ctaUrl,
        },
      });
    } else if (!isMobile) {
      window.open(ctaUrl, '_blank');

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: ctaUrl,
        additional_properties: {
          ...baseTrackingProperties.additional_properties,
          cta_url: ctaUrl,
        },
      });
    } else if (attachable.cta_type === CtaTypeEnum.NATIVE_LINK) {
      sdkClient?.redirectToContent({
        value: '',
        type: LinkTypeEnum.NATIVE_LINK,
        options: {
          url: ctaUrl || '',
        },
      });

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: ctaUrl,
        additional_properties: {
          ...baseTrackingProperties.additional_properties,
          cta_url: ctaUrl,
        },
      });
      return attachable.cta_type;
    } else if (hasOpenExternalLink) {
      sdkClient?.openExternalLink(ctaUrl);

      trackWebClick({
        ...baseTrackingProperties,
        click_type: 'navigation',
        destination_route: ctaUrl,
        additional_properties: {
          ...baseTrackingProperties.additional_properties,
          cta_url: ctaUrl,
        },
      });

      return attachable.cta_type || '';
    }

    sdkClient?.header?.showHeader();
    sdkClient?.header?.setHeader('', HEADER_BUTTON_MODE.HISTORY);

    return attachable.cta_type || '';
  }, [ctaUrl, isMobile, attachable.cta_type, sdkClient, getParams, trackWebClick, baseTrackingProperties, hasOpenExternalLink]);

  const getRsvpRequestType: () => UPDATE_EVENT_POST_TYPE = useCallback((): UPDATE_EVENT_POST_TYPE => {
    const noUserRsvp = attachable.claims_details?.user_count === 0;
    const belowMaxCapacity = attachable.capacity ? (attachable.claims_details?.count ?? 0) < attachable.capacity : true;
    if (noUserRsvp && belowMaxCapacity) {
      return UPDATE_EVENT_POST_TYPE.ATTEND;
    }
    return UPDATE_EVENT_POST_TYPE.RECALL;
  }, [attachable.capacity, attachable.claims_details?.count, attachable.claims_details?.user_count]);

  const userBelowMaxUserClaims = useMemo(() => Boolean(attachable.max_claims_per_user ? (attachable.claims_details?.user_count ?? 0) < (attachable.max_claims_per_user || 1) : true), [
    attachable.max_claims_per_user,
    attachable.claims_details?.user_count,
  ]);
  const belowMaxClaimCapacity = useMemo(
    () => Boolean(attachable.max_claims_total ? (attachable.claims_details?.count ?? 0) < attachable.max_claims_total : true),
    [attachable.claims_details?.count, attachable.max_claims_total],
  );

  const getClaimRequestType: () => UPDATE_OFFER_TYPE = useCallback((): UPDATE_OFFER_TYPE => {
    if (userBelowMaxUserClaims && belowMaxClaimCapacity && !showCancelTextOverride) {
      return UPDATE_OFFER_TYPE.CLAIM;
    }
    return UPDATE_OFFER_TYPE.CANCEL;
  }, [belowMaxClaimCapacity, userBelowMaxUserClaims, showCancelTextOverride]);

  const getClaimCta: () => string = useCallback((): string => {
    if (attachable.rsvp_enabled) {
      const type = getRsvpRequestType();
      setUpdateEventPostType(type);
      updateEventPost({ type, uuid: contentUuid });
    }
    if (attachable.claimable) {
      const type = getClaimRequestType();
      setUpdateOfferType(type);
      updateOffer({ type, uuid: contentUuid });
    }
    return CtaTypeEnum.CLAIM;
  }, [
    attachable.claimable,
    attachable.rsvp_enabled,
    contentUuid,
    updateEventPost,
    updateOffer,
    getClaimRequestType,
    getRsvpRequestType,
  ]);

  const getOnClick: (type: CtaTypeEnum | undefined | '') => string | Window | void = useCallback(
    (type: CtaTypeEnum | undefined | '') => {
      switch (type) {
      case CtaTypeEnum.INTERNAL_LINK:
        return getInternalLink();
      case CtaTypeEnum.MICRO_FRONTEND:
        return getMicroFrontend();
      case CtaTypeEnum.CLAIM:
        return getClaimCta();
      default:
        return getExternalUrlOrDeepLink();
      }
    },
    [getClaimCta, getExternalUrlOrDeepLink, getInternalLink, getMicroFrontend],
  );

  const onClickButton = useCallback(() => {
    getOnClick(attachable?.cta_type || '');
    setErrorMessage(undefined);
  }, [attachable?.cta_type, getOnClick, setErrorMessage]);

  const refetchContent = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['content', contentUuid] });
  }, [queryClient, contentUuid]);

  const onClickDoneButton = useCallback(() => {
    toggle();
    setErrorMessage(undefined);

    setTimeout(() => {
      setConfirmedModalFalse();
      disableCancelTextOverride();
      resetUpdateEventPost();
      resetUpdateOffer();
    }, 500);
  }, [disableCancelTextOverride, resetUpdateEventPost, resetUpdateOffer, setConfirmedModalFalse, toggle]);

    const onCtaClick = useCallback(() => {
      if (attachable.cta_type === CtaTypeEnum.CLAIM) {
        toggle();
        trackWebClick(baseTrackingProperties);
      } else {
        onClickButton();
      }
    }, [attachable.cta_type, baseTrackingProperties, onClickButton, toggle, trackWebClick]);

  const onCancelClick = useCallback(() => {
    toggle();
    disableCancelTextOverride();
    setErrorMessage(undefined);
  }, [disableCancelTextOverride, toggle, setErrorMessage]);

  useEffect(() => {
    const eventPostFulfilled = updateEventPostStatus === 'success';
    const eventPostRejected = updateEventPostStatus === 'error';
    const offerFulfilled = updateOfferStatus === 'success';
    const offerRejected = updateOfferStatus === 'error';

    const eventPostFulfilledAttend = eventPostFulfilled && updateEventPostType === UPDATE_EVENT_POST_TYPE.ATTEND;
    const eventPostRejectedAttend = eventPostRejected && updateEventPostType === UPDATE_EVENT_POST_TYPE.ATTEND;

    const eventPostFulfilledRecall = eventPostFulfilled && updateEventPostType === UPDATE_EVENT_POST_TYPE.RECALL;
    const eventPostRejectedRecall = eventPostRejected && updateEventPostType === UPDATE_EVENT_POST_TYPE.RECALL;

    const offerFulfilledClaim = offerFulfilled && updateOfferType === UPDATE_OFFER_TYPE.CLAIM;
    const offerRejectedClaim = offerRejected && updateOfferType === UPDATE_OFFER_TYPE.CLAIM;
    const offerFulfilledCancel = offerFulfilled && updateOfferType === UPDATE_OFFER_TYPE.CANCEL;
    const offerRejectedCancel = offerRejected && updateOfferType === UPDATE_OFFER_TYPE.CANCEL;

    if (eventPostRejectedAttend) {
      setErrorMessage(intl.formatMessage({ id: 'rsvps.unable_to_confirm_RSVP' }));
    }

    if (eventPostRejectedRecall) {
        setErrorMessage(intl.formatMessage({ id: 'rsvps.unable_to_cancel_RSVP' }));
      }

    if (offerRejectedClaim) {
      setErrorMessage(intl.formatMessage({ id: 'claims.unable_to_claim_offer' }));
    }

    if (offerRejectedCancel) {
      setErrorMessage(intl.formatMessage({ id: 'claims.unable_to_cancel_claim' }));
    }

    if (eventPostFulfilledAttend || offerFulfilledClaim) {
      setConfirmedModalTrue();
      refetchContent();
    }
    if (eventPostFulfilledRecall || offerFulfilledCancel) {
      disableCancelTextOverride();
      onClickDoneButton();
      refetchContent();
    }
  }, [disableCancelTextOverride, intl, onClickDoneButton, refetchContent, setConfirmedModalTrue, updateEventPostStatus, updateEventPostType, updateOfferStatus, updateOfferType]);

  const isClaimable = useMemo(() => {
    if (attachable.claimable) {
      return userBelowMaxUserClaims && belowMaxClaimCapacity;
    }
    return Boolean(attachable.claimable);
  }, [attachable.claimable, belowMaxClaimCapacity, userBelowMaxUserClaims]);

  const showCancelClaimButton = useMemo(() =>
   (attachable.claims_details?.user_count ?? 0) > 0,
  [attachable.claims_details?.user_count]);

  const onCancelClaimClick = useCallback(() => {
    enableCancelTextOverride();
    toggle();
  }, [enableCancelTextOverride, toggle]);

  return {
    isVisible,
    isConfirmedModalVisible,
    showCancelClaimButton,
    onCancelClaimClick,
    showCancelTextOverride,
    onCancelClick,
    buttonIsLoading: updateEventPostStatus === 'pending' || updateOfferStatus === 'pending',
    onClickButton: isConfirmedModalVisible ? onClickDoneButton : onClickButton,
    onCtaClick,
    isClaimable,
    isExternalUrl,
    hasOpenExternalLink,
    updateEventPostStatus,
    updateOfferStatus,
    errorMessage,
    ctaUrl,
  };
};
